import { faArrowRight, faBarcodeRead, faBox, faDollyEmpty, faDollyFlatbedAlt, faExchangeAlt, faHandHoldingBox, faPalletAlt, faSearch, faTasks, faTimes, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton, CheckboxList, BarcodeSearch, RecapPopup, DashboardOverviewDetails, DetailsCountCard } from 'components';
import { TripContext } from 'contexts';
import notify from 'devextreme/ui/notify';
import { ItemVerificationView } from 'enums';
import React, { FC, Fragment, useContext, useState } from 'react';
import { useNavigation, useOrderItemService, useParamService, useReturnItemService } from 'services';
import { OrderType, Temperature } from 'services/enums';
import { EquipmentType as StopEquipmentType, LicensePlate, LineItem, Mispick, Order, OrderItem, OrderItemChange, ReturnItem, StopEquipment } from 'services/types';
import { CheckBoxItem, IconOptions } from 'types';
import { EQUIPMENT_MAX_QUANTITY } from 'utils';
import './stop-overview.scss';
import { PalletsRegular } from 'services/types/equipments';

export const StopOverviewPage: FC = () => {

    const { navigateTo } = useNavigation()
    const { getIntValue, getStringValue } = useParamService
    const { isReturnItemCompleted } = useReturnItemService()
    const tripContext = useContext(TripContext)
    const { isOrderItemChangeComplete, isOrderItemComplete } = useOrderItemService()
    const tripId: number | undefined = getIntValue('tripId')
    const stopId: number | undefined = getIntValue('stopId')
    const [isRecapPopupVisible, setIsRecapPopupVisible] = useState<boolean>(false)

    const [temperatures, setTemperatures] = useState<CheckBoxItem[]>([
        { id: Temperature.Dry, isSelected: (getStringValue('dry') == 'true'), text: 'Dry', isEnable: true },
        { id: Temperature.Frozen, isSelected: (getStringValue('frozen') == 'true'), text: 'Frozen', isEnable: true },
        { id: Temperature.Refrigerated, isSelected: (getStringValue('ref') == 'true'), text: 'Ref', isEnable: true }
    ])

    const orders: Order[] = tripContext.orders.filter(lp => lp.stopId === stopId) ?? []
    const stopItems: OrderItem[] = tripContext.orderItems.filter(item => orders.some(order => order.id === item.orderId))

    const returnItems: ReturnItem[] = tripContext.returnItems.filter(returnItem => orders.some(order => order.id === returnItem.orderId)) ?? []
    const lineItems: LineItem[] = tripContext.lineItems.filter(lineItem => (stopItems.some(orderItem => lineItem.id === orderItem.lineItemId) || returnItems.some(returnItem => lineItem.id === returnItem.lineItemId))) ?? []
    const stopLps: LicensePlate[] = tripContext.licensePlates.filter(lp => lp.stopId == stopId) ?? []
    const stopEquipments: StopEquipment[] = tripContext.stopEquipments.filter(equipment => equipment.stopId === stopId) ?? []
    const mispicksItems: Mispick[] = tripContext.mispicks.filter(m => m.stopId === stopId) ?? []
    const orderItemChanges: OrderItemChange[] = tripContext.orderItemChanges.filter(change => stopItems.some(orderItem => orderItem.id == change.orderItemId)) ?? []
    const equipmentTypes: StopEquipmentType[] = tripContext.equipmentTypes.toArray() ?? []

    const temperatureItems: OrderItem[] = stopItems.filter(orderItem => temperatures.some(temp => temp.id == lineItems.find(lineItem => lineItem.id == orderItem.lineItemId)?.temperature && temp.isSelected))
    const temperatureReturnItems: ReturnItem[] = returnItems.filter(ri => temperatures.some(temp => temp.id == lineItems.find(lineItem => lineItem.id == ri.lineItemId)?.temperature && temp.isSelected))
    const temperatureLps: LicensePlate[] = stopLps.filter(lp => lp.verifiedMethod == null && temperatures.some(temp => temp.id == lp.temperature && temp.isSelected))
    const notVerifedResult: OrderItem[] = temperatureItems.filter(items => !isOrderItemComplete(items));
    const shortedItemsResult: OrderItem[] = temperatureItems.filter(items => items.shippedQuantity < items.orderedQuantity);
    const changesResult: OrderItem[] = temperatureItems.filter(items => (items.shippedQuantity !== items.countedQuantity || (orderItemChanges.filter(changes => changes.orderItemId == items.id) && orderItemChanges.filter(changes => changes.orderItemId == items.id).length > 0)));
    const equipmentResult: StopEquipment[] = stopEquipments.filter(equipment => equipment.quantityOut != null && equipment.quantityOut <= EQUIPMENT_MAX_QUANTITY)

    const regularPalletCount: number | undefined = stopEquipments.find(equipment => equipment.equipmentTypeId == PalletsRegular.Id)?.quantityIn

    const isTempSelected = (temp: Temperature | undefined): boolean => temperatures.find(t => t.id === temp)?.isSelected ?? false

    const onClick = (selectedCard: ItemVerificationView): void => {
        let title: string = ''
        switch (selectedCard) {
            case ItemVerificationView.NotVerified: title = 'Not Verified'; break;
            case ItemVerificationView.Changes: title = 'Changed Items'; break;
            case ItemVerificationView.LPs: title = 'LPs'; break;
            case ItemVerificationView.ShortedItems: title = 'Shorted Items'; break;
            case ItemVerificationView.Search: title = 'Search'; break;
            case ItemVerificationView.Returns: title = 'Returns'; break;
            default: 'Item Verification'; break;
        }
        navigateTo(`item-verification?stopId=${stopId}&searchId=${selectedCard}&dry=${temperatures[0].isSelected}&frozen=${temperatures[1].isSelected}&ref=${temperatures[2].isSelected}`, title)
    }

    const handleSelectedTempsChanged = (selectedTemp: string | number, isSelected: boolean): void => {
        let tempIndex: number = temperatures.findIndex(temp => temp.id == selectedTemp)
        temperatures[tempIndex].isSelected = isSelected
        setTemperatures([...temperatures])
        navigateTo(`stop-overview?tripId=${tripId}&stopId=${stopId}&dry=${temperatures[0].isSelected}&frozen=${temperatures[1].isSelected}&ref=${temperatures[2].isSelected}`)
    }

    const checkEquipmentShortReasonId = (): boolean => {

        let result: boolean = false
        equipmentTypes.forEach(type => {
            if (type.isShortReasonRequired) {
                result = stopEquipments.some(equipment => equipment.equipmentTypeId == type.id && equipment.quantityOut! >= 0 && equipment.quantityOut! < equipment.quantityIn && equipment.equipmentShortReasonId == null);
            }
        })
        return result
    }

    const areAnyEquipmentsOverMaxQuantity = (): boolean => { 
        return stopEquipments.some(equipment => equipment.quantityOut! > EQUIPMENT_MAX_QUANTITY)
    }

    const onClickRecapPage = (): void => {
        let incompleteOrderItems: OrderItem[] = stopItems.filter(item => !isOrderItemComplete(item))
        let incompleteReturnItems: ReturnItem[] = returnItems.filter(item => !isReturnItemCompleted(item));
        let incompleteLps: LicensePlate[] = stopLps.filter(lps => lps.verifiedMethod == null)

        let stopTemps: Temperature[] = []
        let stopOrderItemsLineItems = lineItems.filter(li => stopItems.some(oi => oi.lineItemId == li.id))
        let stopReturnItemsLineItems = lineItems.filter(li => returnItems.some(ri => ri.lineItemId == li.id))        
        let stopLineItems: LineItem[] = [...stopOrderItemsLineItems, ...stopReturnItemsLineItems]
        
        stopLineItems.forEach(lp => {
            if (!stopTemps.some(temp => temp === lp.temperature)) {
                stopTemps.push(lp.temperature)
            }
        })

        let completeTemperatures: Temperature[] = []
        const isStcStop: boolean = tripContext.stops.find(s => s.id === stopId)?.orderTypes?.some(item => item == OrderType.SubjectToCount) ?? false
        let lineItemsWithIncompleteOi: LineItem[] = lineItems.filter(item => (incompleteReturnItems.some(ri => ri.lineItemId === item.id)
        || incompleteOrderItems.some(oi => oi.lineItemId === item.id)))
        stopTemps.forEach(temp => {
            const areTempItemsVerified: boolean = !lineItemsWithIncompleteOi.some(li => li.temperature === temp)
            const areTempLpsVerified: boolean = !incompleteLps.some(li => li.temperature === temp)
            if (areTempItemsVerified && (areTempLpsVerified || !isStcStop)) {
                completeTemperatures.push(temp)
            }
        })

        let areOrderItemsComplete: boolean = incompleteOrderItems.length === 0
        let isEquipmentComplete: boolean = equipmentResult.length == stopEquipments.length && !checkEquipmentShortReasonId()
        let areAllTempsComplete: boolean = completeTemperatures.length === stopTemps.length ? true : false
        let areReturnItemsComplete: boolean = incompleteReturnItems.length === 0

        if (stopItems.length > 0 && completeTemperatures.length === 0) {
            notify(`Please complete all the items on at least one of your temperatures before moving to recap`, 'info', 5000)
        }
        else if (areOrderItemsComplete && areReturnItemsComplete && areAllTempsComplete && !isEquipmentComplete) {
            notify(`Please complete your load equipment before moving to recap`, 'info', 5000)
        }
        else if (stopTemps.length === 1 && areAllTempsComplete) {
            let incompleteChanges: OrderItemChange[] = orderItemChanges.filter(change => !isOrderItemChangeComplete(change))
            const notApprovedChanges: OrderItemChange[] = orderItemChanges.filter(change => change.isChangeApproved != true)
            if (incompleteChanges.length > 0) {
                notify(`Please complete unfinished changes`, 'info', 5000)
            }
            else if(notApprovedChanges.length > 0) {
                navigateTo(`change-approval?tripId=${tripId}&stopId=${stopId}&dry=true&frozen=true&ref=true`)
            }
            else
                navigateTo(`recap?tripId=${tripId}&stopId=${stopId}&readOnly=false&dry=true&frozen=true&ref=true`)
        }
        else {
            setIsRecapPopupVisible(true)
        }
    }

    let changesHeaderIcon = (): IconOptions | undefined => {
        let isNotComplete: OrderItemChange[] = orderItemChanges.filter(change => {
            const orderItem = stopItems.find(oi => oi.id === change.orderItemId)
            const lineItem = lineItems.find(li => li.id === orderItem?.lineItemId)
            return !isOrderItemChangeComplete(change) && isTempSelected(lineItem?.temperature)
        })

        let icon: IconOptions | undefined = isNotComplete.length > 0 ? { icon: faTimesCircle, color: '#EB5757', size: '1x' } : undefined
        return icon
    }

    let returnsHeaderIcon = (): IconOptions | undefined => {
        let incompleteReturns: ReturnItem[] = returnItems.filter(rtn => !isReturnItemCompleted(rtn) && isTempSelected(lineItems.find(li => li.id == rtn.lineItemId)?.temperature))
        let icon: IconOptions | undefined = incompleteReturns.length > 0 ? { icon: faTimesCircle, color: '#EB5757', size: '1x' } : undefined
        return icon
    }

    let equipmentHeaderIcon = (): IconOptions | undefined => {
      let icon: IconOptions | undefined = equipmentResult.length == 0 ? undefined : (checkEquipmentShortReasonId() || areAnyEquipmentsOverMaxQuantity() ? { icon: faTimesCircle, color: '#EB5757', size: '1x' } : undefined)
        return icon
    }

    return (
        <Fragment>
            <BarcodeSearch tripId={tripId} stopId={stopId} />
            <div className={'stopoverview-heading-card'}>
                <div className={'temperature-selection-card'}>
                    <CheckboxList data={temperatures} onValueChanged={handleSelectedTempsChanged} />
                </div>
                <div className={'search-card'}>
                    <ActionButton
                        icon={faSearch}
                        iconColor={'#FFA90A'}
                        buttonColor={'#F1F1F1'}
                        buttonText='Search'
                        buttonTextColor={'black'}
                        onClick={() => onClick(ItemVerificationView.Search)}
                    />
                </div>
            </div>
            <div className={'details-section'}>
                <div className={'details-section-column'}>
                    {temperatureItems &&
                        <DashboardOverviewDetails
                            items={temperatureItems}
                            lps={stopLps.filter(lp => temperatures.some(temp => temp.id == lp.temperature && temp.isSelected))}
                        />
                    }
                    <DetailsCountCard
                        icon={faBarcodeRead}
                        title={'Mispicks'}
                        value={mispicksItems.length}
                        onClick={() => navigateTo(`mispicks?tripId=${tripId}&stopId=${stopId}`)} />
                    <DetailsCountCard
                        id={ItemVerificationView.Returns}
                        icon={faHandHoldingBox}
                        title={'Returns'}
                        value={temperatureReturnItems.length}
                        rightHandIcon={returnsHeaderIcon()}
                        onClick={(id?: ItemVerificationView) => id && onClick(id)} />
                </div>
                <div className={'details-section-column'}>
                    <DetailsCountCard
                        id={ItemVerificationView.NotVerified}
                        icon={faBox}
                        title={'Not Verified'}
                        value={notVerifedResult.length}
                        onClick={(id?: ItemVerificationView) => id && onClick(id)} />
                    <DetailsCountCard
                        id={ItemVerificationView.LPs}
                        icon={faPalletAlt}
                        title={'LPs'}
                        value={temperatureLps.filter(lp => lp.verifiedMethod == null).length}
                        onClick={(id?: ItemVerificationView) => id && onClick(id)} />
                    <DetailsCountCard
                        id={ItemVerificationView.ShortedItems}
                        icon={faDollyEmpty}
                        title={'Shorted Items'}
                        value={shortedItemsResult.length}
                        onClick={(id?: ItemVerificationView) => id && onClick(id)} />
                    <DetailsCountCard
                        id={ItemVerificationView.Changes}
                        icon={faExchangeAlt}
                        title={'Changes'}
                        value={changesResult.length}
                        rightHandIcon={changesHeaderIcon()}
                        onClick={(id?: ItemVerificationView) => id && onClick(id)} />
                    <DetailsCountCard
                        icon={faDollyFlatbedAlt}
                        title={'Equipment'}
                        renderContent={() => <div className={'stop-overview-equipment'}>{`Pallets: ${regularPalletCount ?? 'N/A'}`}</div>}
                        rightHandIcon={equipmentHeaderIcon()}
                        onClick={() => navigateTo(`load-equipments?stopId=${stopId}`)} />
                </div>
            </div>
            <div className={'bottom-fix-action-row'}>
                <ActionButton
                    icon={faTimes}
                    buttonColor={'#F2994A'}
                    buttonText='Mass Refuse'
                    onClick={() => navigateTo(`mass-refuse?stopId=${stopId}`)}
                />
                <ActionButton
                    icon={faTasks}
                    buttonColor={'#EB5757'}
                    buttonText='Mass Verify'
                    onClick={() => navigateTo(`mass-verify?stopId=${stopId}`)}
                />
                <ActionButton
                    icon={faArrowRight}
                    buttonColor={'#82BC24'}
                    buttonText='Next'
                    onClick={onClickRecapPage}
                />
            </div>
            <RecapPopup
                visible={isRecapPopupVisible}
                tripId={tripId}
                stopId={stopId}
                isNavFromStopOverview={true}
                onClickCancel={() => setIsRecapPopupVisible(false)} />
        </Fragment>
    )
};